// v3 tree farm, authored the way age-of-mcp writes it: a 4x4 lattice of small
// conifer-round trees in strict rows on a tilled bed, with the south-east plot
// harvested (stump + log pile) and a rail fence ringing the grove. The library
// has no terrain, so the bed and every tree/rail/log sit on FLAT ground (the
// look the flattened source baked); the ground-tile helpers it needs (gridInfo,
// gridSurfaceY, warpedGridSlab) are inlined here with the terrain coupling
// stripped, since the lib has no terrain module. Canopies wear the wood-industry
// leaf green. Ported 1:1 from age-of-mcp's v3/tree_farm.ts.
import * as THREE from "three";
import { defineModel, pace, styleV3, type Part } from "../../lib";

const { ROOF_WOODIND, TIMBER, WOOD, WOOD_DK } = styleV3;

// --- Inlined ground-tile helpers (flat, terrain coupling stripped) -----------
type Corners = readonly [number, number, number, number];
type V = readonly [number, number, number];
const FLAT: Corners = [0, 0, 0, 0];
type Grid = { grid: readonly number[]; n: number };
function gridInfo(heights?: readonly number[]): Grid {
  if (heights && heights.length >= 4) {
    const side = Math.round(Math.sqrt(heights.length));
    if (side * side === heights.length && side >= 2) return { grid: heights, n: side - 1 };
  }
  return { grid: FLAT, n: 1 };
}
function gridSurfaceY(lx: number, lz: number, half: number, grid: readonly number[], n: number): number {
  const s = n + 1;
  const u = ((lx / half + 1) / 2) * n;
  const v = ((lz / half + 1) / 2) * n;
  let i = Math.floor(u), j = Math.floor(v);
  i = Math.max(0, Math.min(n - 1, i));
  j = Math.max(0, Math.min(n - 1, j));
  const fu = u - i, fv = v - j;
  const sw = grid[j * s + i]!, se = grid[j * s + i + 1]!;
  const nw = grid[(j + 1) * s + i]!, ne = grid[(j + 1) * s + i + 1]!;
  return sw * (1 - fu) * (1 - fv) + se * fu * (1 - fv) + nw * (1 - fu) * fv + ne * fu * fv;
}
function quad(out: number[], p0: V, p1: V, p2: V, p3: V): void {
  out.push(...p0, ...p1, ...p2, ...p0, ...p2, ...p3);
}
function warpedGridSlab(
  half: number, thickness: number, color: string,
  grid: readonly number[], n: number, lift = 0,
): Part {
  const s = n + 1;
  const lc = (k: number): number => -half + (k / n) * 2 * half;
  const top = (i: number, j: number): number => grid[j * s + i]! + lift;
  let yb = Infinity;
  for (const g of grid) yb = Math.min(yb, g);
  yb -= thickness;
  const pos: number[] = [];
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const x0 = lc(i), x1 = lc(i + 1), z0 = lc(j), z1 = lc(j + 1);
      const tSW: V = [x0, top(i, j),     z0], tSE: V = [x1, top(i + 1, j),     z0];
      const tNW: V = [x0, top(i, j + 1), z1], tNE: V = [x1, top(i + 1, j + 1), z1];
      const bSW: V = [x0, yb, z0], bSE: V = [x1, yb, z0], bNW: V = [x0, yb, z1], bNE: V = [x1, yb, z1];
      quad(pos, tSW, tNW, tNE, tSE);
      quad(pos, bSW, bSE, bNE, bNW);
      quad(pos, tNW, bNW, bNE, tNE);
      quad(pos, tSE, bSE, bSW, tSW);
      quad(pos, tSE, tNE, bNE, bSE);
      quad(pos, tSW, bSW, bNW, tNW);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  return { geometry, color, position: [0, 0, 0] };
}

// --- Tree farm (1:1 from the source) -----------------------------------------
const SOIL   = "#5c4327";
const FURROW = "#49351d";
const STUMP  = "#c9a06a";

const HALF = 1.92;
const SPOTS = [-1.38, -0.46, 0.46, 1.38] as const;
const CUT_X = 1.38, CUT_Z = -1.38;

function surf(g: Grid, x: number, z: number): number {
  return gridSurfaceY(x, z, HALF, g.grid, g.n);
}

function soil(g: Grid): Part {
  return warpedGridSlab(HALF, 0.12, SOIL, g.grid, g.n, 0);
}

function furrows(g: Grid): Part[] {
  return SPOTS.map(z => ({
    geometry: new THREE.BoxGeometry(HALF * 1.92, 0.04, 0.1),
    color: FURROW,
    position: [0, surf(g, 0, z) + 0.05, z] as [number, number, number],
  }));
}

function tree(g: Grid, x: number, z: number, k: number): Part[] {
  const y0 = surf(g, x, z) + 0.04;
  const th = 0.14 + 0.28 * k;
  const cr = 0.1 + 0.26 * k;
  const ch = 0.22 + 0.52 * k;
  return [
    { geometry: new THREE.CylinderGeometry(0.05, 0.065, th, 5), color: TIMBER,
      position: [x, y0 + th / 2, z] },
    { geometry: new THREE.ConeGeometry(cr, ch, 6), color: ROOF_WOODIND,
      position: [x, y0 + th + ch / 2 - 0.03, z] },
  ];
}

function saplings(g: Grid): Part[] {
  const out: Part[] = [];
  for (const x of SPOTS) {
    for (const z of SPOTS) {
      out.push({ geometry: new THREE.ConeGeometry(0.07, 0.18, 5), color: ROOF_WOODIND,
        position: [x, surf(g, x, z) + 0.13, z] });
    }
  }
  return out;
}

function grove(g: Grid, k: number, harvested: boolean): Part[] {
  const out: Part[] = [];
  for (const x of SPOTS) {
    for (const z of SPOTS) {
      if (harvested && x === CUT_X && z === CUT_Z) continue;
      out.push(...tree(g, x, z, k));
    }
  }
  return out;
}

function harvestCorner(g: Grid): Part[] {
  const y0 = surf(g, CUT_X, CUT_Z) + 0.04;
  const ly = surf(g, 0.95, -1.45) + 0.04;
  const log = (y: number, z: number): Part => ({
    geometry: new THREE.CylinderGeometry(0.075, 0.075, 0.56, 6),
    color: WOOD,
    position: [0.95, ly + 0.08 + y, z] as [number, number, number],
    rotation: [0, 0, Math.PI / 2] as [number, number, number],
  });
  return [
    { geometry: new THREE.CylinderGeometry(0.085, 0.1, 0.14, 6), color: STUMP,
      position: [CUT_X, y0 + 0.07, CUT_Z] },
    log(0, -1.38), log(0, -1.52), log(0.13, -1.45),
  ];
}

function rails(g: Grid): Part[] {
  const e = 1.86, len = 2 * e;
  return [
    { geometry: new THREE.BoxGeometry(len, 0.05, 0.06), color: WOOD_DK,
      position: [0, surf(g, 0, -e) + 0.22, -e] },
    { geometry: new THREE.BoxGeometry(len, 0.05, 0.06), color: WOOD_DK,
      position: [0, surf(g, 0, e) + 0.22, e] },
    { geometry: new THREE.BoxGeometry(0.06, 0.05, len), color: WOOD_DK,
      position: [-e, surf(g, -e, 0) + 0.22, 0] },
    { geometry: new THREE.BoxGeometry(0.06, 0.05, len), color: WOOD_DK,
      position: [e, surf(g, e, 0) + 0.22, 0] },
  ];
}

function fieldPoles(g: Grid): Part[] {
  const out: Part[] = [];
  for (const x of [-1.7, 1.7]) {
    for (const z of [-1.7, 1.7]) {
      out.push({
        geometry: new THREE.CylinderGeometry(0.022, 0.022, 0.8, 4), color: TIMBER,
        position: [x, surf(g, x, z) + 0.4, z],
      });
    }
  }
  return out;
}

function partsFor(s: number, heights?: readonly number[]): Part[] {
  const g = gridInfo(heights);
  if (s <= 0) return [soil(g), ...furrows(g)];
  if (s === 1) return [soil(g), ...furrows(g), ...saplings(g)];
  if (s === 2) return [soil(g), ...grove(g, 0.55, false), ...fieldPoles(g)];
  return [
    soil(g),
    ...grove(g, 1, true),
    ...harvestCorner(g),
    ...rails(g),
  ];
}

export const treeFarmV3 = defineModel({
  id: "tree-farm-v3",
  tiles: { w: 4, h: 4 },
  batchId: "45-optimized",
  name: "Tree farm (v3)",
  tags: ["building","tree","farm","low-poly","age-of-mcp","v3"],
  summary: "Low-poly tree farm.",
  description: "A timber plantation of planted trees grown for wood.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "grove", position: [0, 0, 0], facing: 0, capacity: 6 },
  ],
  interactions: [
    { id: "tend", label: "Tending the saplings", station: "grove", variants: ["complete"], ...pace([[-1.55, -1.55], [1.55, -1.55], [1.55, 0], [-1.55, 0], [-1.55, 1.55], [1.55, 1.55]]) },
  ],
});
